import { Space, Stack, Tooltip, UnstyledButton } from '@mantine/core'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import { useRoute } from 'wouter'

import ProdLogo from '#assets/images/blue-logomark.svg'
import { NavAnchor } from '#components/NavAnchor'
import { FeatureFlags } from '#components/Sidebar/FeatureFlags'
import { HelpMenu } from '#components/Sidebar/HelpMenu'
import { UserMenu } from '#components/Sidebar/UserMenu'
import { getEnvLogoOverride } from '#config'
import { useMst } from '#state'
import { ExplorerViewType } from '#state/types'
import { ROUTE_PATTERNS } from '#util/pathConfigs'

import styles from './ExplorerSidestrip.module.scss'

const configs: Record<
  ExplorerViewType,
  {
    tooltip: string
    icon: string
  }
> = {
  browser: {
    tooltip: 'Folders and documents',
    icon: 'fas fa-folder',
  },
  outline: {
    tooltip: 'Outline',
    icon: 'fas fa-list',
  },
  pages: {
    tooltip: 'Pages',
    icon: 'fas fa-files',
  },
  search: {
    tooltip: 'Search',
    icon: 'fas fa-magnifying-glass',
  },
}

const SidestripButton = ({
  view,
  active,
  disabled,
  onClick,
}: {
  view: ExplorerViewType
  active: boolean
  onClick: (view: ExplorerViewType) => void
  disabled?: boolean
}) => (
  <Tooltip label={configs[view].tooltip} position="right" withinPortal>
    <UnstyledButton
      disabled={disabled}
      className={cn(styles.explorerButtons_button, {
        [styles.explorerButtons_button__selected]: active,
      })}
      onClick={() => onClick(view)}
    >
      <i className={configs[view].icon}></i>
    </UnstyledButton>
  </Tooltip>
)

export const ExplorerSidestrip = observer(function ExplorerSidestrip({
  currentView,
  onSelectView,
  enabledViews,
}: {
  currentView?: ExplorerViewType
  onSelectView: (view: ExplorerViewType) => void
  enabledViews: ExplorerViewType[]
}) {
  const { currentScript, environment } = useMst()
  const [isEditorRoute] = useRoute(ROUTE_PATTERNS.scriptEditor)
  const showShortcuts = isEditorRoute && !currentScript?.isInk
  const logo = getEnvLogoOverride(environment.config.name) ?? ProdLogo

  return (
    <Stack justify="space-between" className={styles.explorerButtons}>
      <Stack w="100%">
        <Stack w="100%" gap={0}>
          <Tooltip label="Go to Dashboard" position="right" withinPortal>
            <div>
              <NavAnchor href="/" className={styles.explorerButtons_logo}>
                <img src={logo} />
              </NavAnchor>
            </div>
          </Tooltip>
          <Space h={4} />
          {enabledViews.map((view) => (
            <SidestripButton
              view={view}
              active={currentView === view}
              onClick={onSelectView}
              key={view}
            />
          ))}
        </Stack>
      </Stack>
      <Stack align="center">
        <FeatureFlags />
        <HelpMenu showShortcuts={showShortcuts} />
        <UserMenu />
        <Space h={5} />
      </Stack>
    </Stack>
  )
})
